import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/Button';
import { Icon, type IconName } from '@/components/Icon';
import {
  listAuthSources,
  login,
  type AuthError,
  type AuthSource,
} from '@/api/auth';
import { useAuth } from '@/store/auth';

const PROVIDER_ICONS: Record<AuthSource, IconName> = {
  CRYSTAL: 'layers',
  MAFA_BO: 'shield',
  CALL_CENTER: 'call',
  LOCAL: 'user',
};

/**
 * Federated login screen. Mirrors the two-column layout from MAFA's
 * auth.jsx, with three concrete differences from the demo:
 *
 *  1. The provider list is fetched from /v1/auth/sources rather than
 *     hardcoded. LOCAL is filtered out of the federated tab — it
 *     belongs to the second segment.
 *  2. Errors propagate from the backend (401, 403, 502) and map to
 *     translated messages.
 *  3. On success we navigate to the /dossiers route; the auth guard on
 *     the layout takes care of the rest.
 */
export function LoginPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [mode, setMode] = useState<'federated' | 'local'>('federated');
  const [sources, setSources] = useState<AuthSource[] | null>(null);
  const [busy, setBusy] = useState<AuthSource | 'local' | null>(null);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<AuthError | null>(null);
  const endReason = useAuth((s) => s.endReason);

  useEffect(() => {
    let cancelled = false;
    listAuthSources()
      .then((list) => {
        if (!cancelled) setSources(list);
      })
      .catch(() => {
        if (!cancelled) setSources([]);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const federatedSources = useMemo(
    () => (sources ?? []).filter((s) => s !== 'LOCAL'),
    [sources],
  );

  async function attempt(sourceSystem: AuthSource, username: string, pwd: string) {
    setBusy(sourceSystem);
    setError(null);
    const result = await login({ sourceSystem, username, password: pwd });
    setBusy(null);
    if (!result.ok) {
      setError(result.reason);
      return;
    }
    void navigate({ to: '/dossiers' });
  }

  async function attemptLocal() {
    if (!email || !password) {
      setError('missingFields');
      return;
    }
    await attempt('LOCAL', email, password);
  }

  return (
    <div className="login-stage">
      <div className="login-aside">
        <div className="brand" style={{ padding: 0, marginBottom: 38 }}>
          <div className="mark">
            <Icon name="layers" />
          </div>
          <div>
            <div className="bt" style={{ color: '#fff' }}>PDE.CI</div>
            <div className="bs" style={{ color: 'oklch(0.92 0.03 184)' }}>
              {t('brand.subtitle')}
            </div>
          </div>
        </div>
        <h1 className="login-h1">{t('auth.sloganTitle')}</h1>
        <p className="login-p">{t('auth.sloganBody')}</p>
        <div className="login-feats">
          <div className="lf">
            <span className="lf-ic"><Icon name="folder" /></span>
            {t('auth.feat1')}
          </div>
          <div className="lf">
            <span className="lf-ic"><Icon name="upload" /></span>
            {t('auth.feat2')}
          </div>
          <div className="lf">
            <span className="lf-ic"><Icon name="shield" /></span>
            {t('auth.feat3')}
          </div>
        </div>
      </div>

      <div className="login-panel">
        <div className="login-card">
          <div style={{ textAlign: 'center', marginBottom: 26 }}>
            <div style={{ fontWeight: 800, fontSize: 22, letterSpacing: '-.025em' }}>
              {t('auth.title')}
            </div>
            <div className="muted" style={{ fontSize: 13.5, fontWeight: 500, marginTop: 6 }}>
              {t('auth.subtitle')}
            </div>
          </div>

          <div className="seg" style={{ width: '100%', marginBottom: 22 }}>
            <button
              className={mode === 'federated' ? 'on' : ''}
              style={{ flex: 1 }}
              onClick={() => { setMode('federated'); setError(null); }}
            >
              {t('auth.modeFederated')}
            </button>
            <button
              className={mode === 'local' ? 'on' : ''}
              style={{ flex: 1 }}
              onClick={() => { setMode('local'); setError(null); }}
            >
              {t('auth.modeLocal')}
            </button>
          </div>

          {/* Pourquoi on se retrouve ici. Sans ce mot, une déconnexion
              automatique ressemble à une panne, et la première réaction est
              d'appeler le support. */}
          {!error && endReason === 'idle' && (
            <div className="muted" style={{ marginBottom: 14, fontSize: 13 }}>
              {t('session.endedIdle')}
            </div>
          )}
          {!error && endReason === 'expired' && (
            <div className="muted" style={{ marginBottom: 14, fontSize: 13 }}>
              {t('session.endedExpired')}
            </div>
          )}

          {error && (
            <div className="err-msg" style={{ marginBottom: 14 }}>
              {t(`auth.errors.${error}`)}
            </div>
          )}

          {mode === 'federated' ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {sources === null && (
                <div className="muted" style={{ fontSize: 13, textAlign: 'center', padding: 12 }}>
                  {t('common.loading')}
                </div>
              )}
              {sources !== null && federatedSources.length === 0 && (
                <div className="muted" style={{ fontSize: 13, textAlign: 'center', padding: 18, lineHeight: 1.5 }}>
                  Aucun système d'origine fédéré n'est configuré pour le moment. Utilisez « Compte local » avec les identifiants fournis par votre administrateur.
                </div>
              )}
              {federatedSources.map((src) => (
                <FederatedSourceForm
                  key={src}
                  source={src}
                  busy={busy === src}
                  onSubmit={(username, pwd) => attempt(src, username, pwd)}
                />
              ))}
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              <div className="field">
                <label>{t('auth.emailLabel')}</label>
                <input
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder={t('auth.emailPlaceholder') ?? ''}
                  autoComplete="username"
                />
              </div>
              <div className="field">
                <label>{t('auth.passwordLabel')}</label>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder={t('auth.passwordPlaceholder') ?? ''}
                  autoComplete="current-password"
                  onKeyDown={(e) => { if (e.key === 'Enter') void attemptLocal(); }}
                />
              </div>
              <Button
                variant="primary"
                style={{ justifyContent: 'center', padding: '12px', fontSize: 14 }}
                onClick={() => void attemptLocal()}
                loading={busy === 'local'}
                icon="logout"
              >
                {t('auth.submit')}
              </Button>
            </div>
          )}
        </div>
        <div className="muted" style={{ fontSize: 12, fontWeight: 600, marginTop: 22 }}>
          {t('auth.footer')}
        </div>
      </div>
    </div>
  );
}

/**
 * Per-source mini-form. Crystal needs the user's email + password —
 * we present a compact inline form so the user doesn't have to expand a
 * modal. When the response from the backend matures (different sources
 * require different fields), this is the natural extension point.
 */
function FederatedSourceForm({
  source,
  busy,
  onSubmit,
}: {
  source: AuthSource;
  busy: boolean;
  onSubmit: (username: string, password: string) => void;
}) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');

  const label = t(`auth.providerLabels.${source}`);
  const hint = t(`auth.providerHints.${source}`);

  return (
    <div>
      <button
        type="button"
        className="provider"
        onClick={() => setOpen((v) => !v)}
        disabled={busy}
      >
        <span className="prov-ic"><Icon name={PROVIDER_ICONS[source]} /></span>
        <span style={{ flex: 1, textAlign: 'left' }}>
          <span className="prov-l">{t('auth.providerContinue', { name: label })}</span>
          <span className="prov-d">{hint}</span>
        </span>
        {busy ? (
          <span className="spin" />
        ) : (
          <Icon name={open ? 'chevdown' : 'chevright'} size={17} style={{ color: 'var(--ink-4)' }} />
        )}
      </button>

      {open && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginTop: 12, padding: '4px 4px 0' }}>
          <div className="field">
            <label>{t('auth.emailLabel')}</label>
            <input
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              placeholder={t('auth.emailPlaceholder') ?? ''}
              autoComplete="username"
            />
          </div>
          <div className="field">
            <label>{t('auth.passwordLabel')}</label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder={t('auth.passwordPlaceholder') ?? ''}
              autoComplete="current-password"
              onKeyDown={(e) => {
                if (e.key === 'Enter' && username && password) onSubmit(username, password);
              }}
            />
          </div>
          <Button
            variant="primary"
            size="sm"
            style={{ justifyContent: 'center' }}
            onClick={() => onSubmit(username, password)}
            loading={busy}
            disabled={!username || !password}
            icon="logout"
          >
            {t('auth.submit')}
          </Button>
        </div>
      )}
    </div>
  );
}
